import React, { useEffect, useMemo, useState } from "react";
import Pagination from "../../components/Pagination";

const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_SORT = "User Type";
const DEFAULT_USER_TYPE = "All";
const DEFAULT_AREA = "All";
const DEFAULT_PERMISSION = "";
const DEFAULT_VIEW_TYPE = "";

function fixPopovers() {
  if (window.$?.fn?.popover) {
    window.$('[data-bs-toggle="popover"]').popover({
      trigger: "hover",
      html: true,
    });
  }
}

function scrollToMain() {
  if (window.jQuery) {
    const main = window.jQuery(".main");

    if (main.length) {
      window.jQuery("html, body").animate({ scrollTop: main.offset().top }, 300);
      return;
    }
  }

  const main = document.querySelector(".main");

  if (main) {
    main.scrollIntoView({ behavior: "smooth", block: "start" });
  }
}

function parseInteger(value, fallbackValue) {
  const parsedValue = Number.parseInt(value, 10);
  return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : fallbackValue;
}

function parseBoolean(value, fallbackValue) {
  if (value == null || value === "") {
    return fallbackValue;
  }

  return String(value).toLowerCase() !== "false";
}

function readQueryState(defaultLiveVersionName, itemPerPageOptions) {
  const query = new URLSearchParams(window.location.search);
  const maxPageSize = Math.max(...itemPerPageOptions);

  return {
    hasSearched: query.has("searched"),
    search: query.get("search") || "",
    userType: query.get("userType") || DEFAULT_USER_TYPE,
    permission: query.get("permission") || DEFAULT_PERMISSION,
    area: query.get("area") || DEFAULT_AREA,
    viewType: query.get("viewType") || DEFAULT_VIEW_TYPE,
    versionName: query.get("versionName") || defaultLiveVersionName,
    currentPage: parseInteger(query.get("currentPage"), 1),
    itemPerPage: Math.min(parseInteger(query.get("itemPerPage"), DEFAULT_PAGE_SIZE), maxPageSize),
    sort: query.get("sort") || DEFAULT_SORT,
    orderByAscending: parseBoolean(query.get("orderByAscending"), true),
  };
}

function buildSearchQueryValues(state, defaultLiveVersionName) {
  const values = [];

  if ((state.search || "").trim()) {
    values.push(["search", state.search.trim()]);
  }

  if ((state.userType || "").trim() && state.userType !== DEFAULT_USER_TYPE) {
    values.push(["userType", state.userType.trim()]);
  }

  if ((state.permission || "").trim()) {
    values.push(["permission", state.permission.trim()]);
  }

  if ((state.area || "").trim() && state.area !== DEFAULT_AREA) {
    values.push(["area", state.area.trim()]);
  }

  if ((state.viewType || "").trim()) {
    values.push(["viewType", state.viewType.trim()]);
  }

  if ((state.versionName || "").trim() && state.versionName !== defaultLiveVersionName) {
    values.push(["versionName", state.versionName.trim()]);
  }

  if (state.currentPage && state.currentPage !== 1) {
    values.push(["currentPage", String(state.currentPage)]);
  }

  if (state.itemPerPage && state.itemPerPage !== DEFAULT_PAGE_SIZE) {
    values.push(["itemPerPage", String(state.itemPerPage)]);
  }

  if ((state.sort || DEFAULT_SORT).trim() !== DEFAULT_SORT) {
    values.push(["sort", state.sort.trim()]);
  }

  if (!state.orderByAscending) {
    values.push(["orderByAscending", String(state.orderByAscending)]);
  }

  return values;
}

function buildPageUrl(baseUrl, state, defaultLiveVersionName) {
  const nextUrl = new URL(baseUrl || window.location.pathname, window.location.origin);
  nextUrl.searchParams.set("searched", "1");

  buildSearchQueryValues(state, defaultLiveVersionName).forEach(([name, value]) => {
    nextUrl.searchParams.set(name, value);
  });

  return `${nextUrl.pathname}${nextUrl.search}`;
}

function buildDownloadUrl(downloadUrl, state, defaultLiveVersionName) {
  const nextUrl = new URL(downloadUrl, window.location.origin);

  buildSearchQueryValues(state, defaultLiveVersionName).forEach(([name, value]) => {
    nextUrl.searchParams.set(name, value);
  });

  return `${nextUrl.pathname}${nextUrl.search}`;
}

function UserTypeDropdown({ options, selectedValue, onChange, label }) {
  const selectedLabel = useMemo(() => {
    if (selectedValue !== DEFAULT_USER_TYPE) {
      const selectedOption = options.find((item) => item?.Type === selectedValue);

      if (selectedOption?.Name) {
        return `${selectedOption.Type} - ${selectedOption.Name}`;
      }
    }

    return selectedValue;
  }, [options, selectedValue]);

  return (
    <div className="radio-btn-group d-flex mt-3">
      <div className="label text-primary font-weight-bolder mt-2">{label}</div>
      <div className="dropdown">
        <button
          className="btn-dropdown dropdown-toggle pr-0"
          type="button"
          id="userTypeDropdown"
          data-bs-toggle="dropdown"
          aria-haspopup="true"
          aria-expanded="false"
        >
          {selectedLabel}
        </button>
        <div className="dropdown-menu" aria-labelledby="userTypeDropdown">
          {[{ Type: DEFAULT_USER_TYPE, Name: "" }, ...options].map((item) => (
            <button
              key={`${item.Type}-${item.Name || "blank"}`}
              className={`dropdown-item ${item.Type === selectedValue ? "active" : ""}`}
              type="button"
              onClick={() => onChange(item.Type)}
            >
              {item.Type}
              {item.Name ? ` - ${item.Name}` : ""}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function SimpleDropdown({ id, label, value, options, onChange, dropdownClassName = "dropdown" }) {
  return (
    <div className="radio-btn-group d-flex mt-3">
      <div className="label text-primary font-weight-bolder mt-2">{label}</div>
      <div className={dropdownClassName}>
        <button className="btn-dropdown dropdown-toggle pr-0" type="button" id={id} data-bs-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
          {value}
        </button>
        <div className="dropdown-menu" aria-labelledby={id}>
          {options.map((item) => (
            <button
              key={`${id}-${item}`}
              className={`dropdown-item ${item === value ? "active" : ""}`}
              type="button"
              onClick={() => onChange(item)}
            >
              {item}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function ToggleGroup({ idPrefix, label, options, value, onChange, allLabel }) {
  return (
    <div className="radio-btn-group d-flex mt-3">
      <div className="label text-primary font-weight-bolder mt-2">{label}</div>
      <div className="d-flex flex-wrap align-items-center mx-n2">
        <div className="radio-btn-wrapper px-2 mt-2">
          <input type="checkbox" id={`${idPrefix}All`} checked={!value} readOnly />
          <label className={`btn btn-outline-primary ${!value ? "active" : ""}`} htmlFor={`${idPrefix}All`} onClick={() => onChange("")}>
            {allLabel}
          </label>
        </div>
        {options.map((item, index) => (
          <div className="radio-btn-wrapper px-2 mt-2" key={`${idPrefix}-${item}-${index}`}>
            <input
              type="radio"
              id={`${idPrefix}${index}`}
              value={item}
              checked={item === value}
              onChange={() => onChange(item)}
            />
            <label className={`btn btn-outline-primary ${item === value ? "active" : ""}`} htmlFor={`${idPrefix}${index}`}>
              {item}
            </label>
          </div>
        ))}
      </div>
    </div>
  );
}

function SortToggle({ label, sortKey, activeSort, orderByAscending, onClick }) {
  return (
    <div
      className={`d-flex align-items-start sort-toggle cursor-pointer ${orderByAscending && activeSort === sortKey ? "desc" : ""}`}
      role="button"
      tabIndex={0}
      onClick={() => onClick(sortKey)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick(sortKey);
        }
      }}
    >
      <span className="mr-2">{label}</span>
      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" className="flex-shrink-0" viewBox="0 0 16 16">
        <path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z" />
      </svg>
    </div>
  );
}

export default function DataPermissionsMatrixIsland({ config }) {
  const initialQueryState = useMemo(
    () => readQueryState(config.defaultLiveVersionName, config.itemPerPageOptions || [2, 5, 10, DEFAULT_PAGE_SIZE, 50]),
    [config.defaultLiveVersionName, config.itemPerPageOptions],
  );
  const [searchInput, setSearchInput] = useState(initialQueryState.search);
  const [search, setSearch] = useState(initialQueryState.search);
  const [userType, setUserType] = useState(initialQueryState.userType);
  const [permission, setPermission] = useState(initialQueryState.permission);
  const [area, setArea] = useState(initialQueryState.area);
  const [viewType, setViewType] = useState(initialQueryState.viewType);
  const [versionName, setVersionName] = useState(initialQueryState.versionName);
  const [currentPage, setCurrentPage] = useState(initialQueryState.currentPage);
  const [itemPerPage, setItemPerPage] = useState(initialQueryState.itemPerPage);
  const [sort, setSort] = useState(initialQueryState.sort);
  const [orderByAscending, setOrderByAscending] = useState(initialQueryState.orderByAscending);
  const [hasSearched, setHasSearched] = useState(initialQueryState.hasSearched);
  const [total, setTotal] = useState(0);
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [selectedItem, setSelectedItem] = useState("");
  const [selectedGlossary, setSelectedGlossary] = useState([]);

  const itemPerPageOptions = config.itemPerPageOptions || [2, 5, 10, DEFAULT_PAGE_SIZE, 50];
  const requestState = {
    search,
    userType,
    permission,
    area,
    viewType,
    versionName,
    currentPage,
    itemPerPage,
    sort,
    orderByAscending,
  };

  const resultsLabel = total === 1 ? config.resultsCountSingleLabel : config.resultsCountPluralLabel;
  const downloadUrl = useMemo(
    () => buildDownloadUrl(config.downloadUrl, requestState, config.defaultLiveVersionName),
    [area, config.defaultLiveVersionName, config.downloadUrl, currentPage, itemPerPage, orderByAscending, permission, search, sort, userType, versionName, viewType],
  );

  useEffect(() => {
    fixPopovers();
  }, [searchResults, selectedGlossary]);

  useEffect(() => {
    const modalElement = document.getElementById("dpmItemDetailsModal");

    if (!modalElement || !window.$) {
      return undefined;
    }

    const handleHidden = () => {
      setSelectedItem("");
      setSelectedGlossary([]);
    };

    window.$(modalElement).on("hidden.bs.modal", handleHidden);

    return () => {
      window.$(modalElement).off("hidden.bs.modal", handleHidden);
    };
  }, []);

  useEffect(() => {
    if (!hasSearched) {
      return undefined;
    }

    let isActive = true;
    const nextUrl = buildPageUrl(config.baseUrl, requestState, config.defaultLiveVersionName);
    window.history.pushState({ path: nextUrl }, document.title, nextUrl);

    async function loadResults() {
      document.documentElement.classList.add("loading");
      setLoading(true);
      setError("");

      try {
        const params = new URLSearchParams();

        buildSearchQueryValues(requestState, config.defaultLiveVersionName).forEach(([name, value]) => {
          params.append(name, value);
        });

        const response = await fetch(`${config.searchApiUrl}?${params.toString()}`, {
          headers: {
            Accept: "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`DPM search request failed with ${response.status}`);
        }

        const data = await response.json();

        if (!isActive) {
          return;
        }

        setUserType(data.userType || DEFAULT_USER_TYPE);
        setPermission(data.permission || DEFAULT_PERMISSION);
        setArea(data.area || DEFAULT_AREA);
        setViewType(data.viewType || DEFAULT_VIEW_TYPE);
        setVersionName(data.versionName || config.defaultLiveVersionName);
        setSort(data.sort || DEFAULT_SORT);
        setOrderByAscending(data.orderByAscending !== false);
        setCurrentPage(parseInteger(data.currentPage, 1));
        setItemPerPage(parseInteger(data.itemPerPage, DEFAULT_PAGE_SIZE));
        setTotal(Number(data.total) || 0);
        setSearchResults(Array.isArray(data.results) ? data.results : []);
        scrollToMain();
      } catch (requestError) {
        if (!isActive) {
          return;
        }

        setSearchResults([]);
        setTotal(0);
        setError(config.errorText || requestError?.message || "Unable to load data permissions matrix results.");
      } finally {
        if (!isActive) {
          return;
        }

        setLoading(false);
        document.documentElement.classList.remove("loading");
      }
    }

    loadResults();

    return () => {
      isActive = false;
      document.documentElement.classList.remove("loading");
    };
  }, [
    area,
    config.baseUrl,
    config.defaultLiveVersionName,
    config.errorText,
    config.searchApiUrl,
    currentPage,
    hasSearched,
    itemPerPage,
    orderByAscending,
    permission,
    reloadKey,
    search,
    sort,
    userType,
    versionName,
    viewType,
  ]);

  function updateFilter(setter, value) {
    setter(value);
    setCurrentPage(1);
    setHasSearched(true);
  }

  function submitSearch() {
    setCurrentPage(1);
    setSearch((searchInput || "").trim());
    setHasSearched(true);
    setReloadKey((currentValue) => currentValue + 1);
  }

  function resetFilters() {
    setSearchInput("");
    setSearch("");
    setUserType(DEFAULT_USER_TYPE);
    setPermission(DEFAULT_PERMISSION);
    setArea(DEFAULT_AREA);
    setViewType(DEFAULT_VIEW_TYPE);
    setVersionName(config.defaultLiveVersionName);
    setCurrentPage(1);
    setItemPerPage(DEFAULT_PAGE_SIZE);
    setSort(DEFAULT_SORT);
    setOrderByAscending(true);
    setHasSearched(true);
    setReloadKey((currentValue) => currentValue + 1);
  }

  function toggleSort(nextSort) {
    if (sort === nextSort) {
      setOrderByAscending((currentValue) => !currentValue);
    } else {
      setSort(nextSort);
      setOrderByAscending(true);
    }

    setCurrentPage(1);
    setHasSearched(true);
  }

  async function openVersionDetails() {
    document.documentElement.classList.add("loading");

    try {
      const nextUrl = new URL(config.versionUrlApi, window.location.origin);
      nextUrl.searchParams.set("versionName", versionName);

      const response = await fetch(`${nextUrl.pathname}${nextUrl.search}`, {
        headers: {
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`DPM version details request failed with ${response.status}`);
      }

      const targetUrl = await response.json();
      window.location.href = targetUrl;
    } catch (requestError) {
      document.documentElement.classList.remove("loading");
      setError(config.versionErrorText || requestError?.message || "Unable to open version details.");
    }
  }

  function downloadVersion() {
    const nextUrl = new URL(config.downloadVersionUrl, window.location.origin);
    nextUrl.searchParams.set("versionName", versionName);
    window.open(`${nextUrl.pathname}${nextUrl.search}`, "_blank", "noopener,noreferrer");
  }

  function openItemDetailsModal(itemName, itemGlossary) {
    setSelectedItem(itemName || "");
    setSelectedGlossary(Array.isArray(itemGlossary) ? itemGlossary : []);

    if (window.$) {
      window.$("#dpmItemDetailsModal").modal("show");
    }
  }

  return (
    <>
      {error ? <div className="alert alert-danger mt-3">{error}</div> : null}
      <div className="border-primary border rounded-xl p-4">
        <div className="h3 mb-0">{config.searchHeading}</div>
        <div className="row">
          <div className="col-lg-6 mt-2">
            <div className="input-search">
              <label htmlFor="filterSearch">
                <input
                  type="search"
                  aria-label={config.searchLabel}
                  placeholder={config.searchPlaceholder}
                  className="form-control border-primary border rounded-lg"
                  id="filterSearch"
                  autoComplete="off"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      submitSearch();
                    }
                  }}
                />
                <button type="button" onClick={submitSearch} className="search-button text-white bg-primary rounded-right" aria-label={config.searchButtonLabel}>
                  <span className="icon-search"></span>
                </button>
              </label>
            </div>
          </div>
          <div className="col-lg-6 mt-2">
            <button type="button" onClick={resetFilters} className="btn btn-link px-0 text-underline">
              {config.resetFiltersLabel}
            </button>
          </div>
        </div>
        <div className="h3 mb-0 mt-3">{config.filterHeading}</div>
        <p className="my-2">{config.filterSubheading}</p>
        <UserTypeDropdown
          options={config.userTypes || []}
          selectedValue={userType}
          onChange={(value) => updateFilter(setUserType, value)}
          label={config.userTypeLabel}
        />
        <SimpleDropdown
          id="areaDropdown"
          label={config.areaLabel}
          value={area}
          options={[DEFAULT_AREA, ...(config.areas || [])]}
          onChange={(value) => updateFilter(setArea, value)}
        />
        <ToggleGroup
          idPrefix="itemPermission"
          label={config.permissionLabel}
          options={config.permissions || []}
          value={permission}
          onChange={(value) => updateFilter(setPermission, value)}
          allLabel={config.allLabel}
        />
        <ToggleGroup
          idPrefix="itemViewType"
          label={config.viewTypeLabel}
          options={config.viewTypes || []}
          value={viewType}
          onChange={(value) => updateFilter(setViewType, value)}
          allLabel={config.allLabel}
        />
        <div className="radio-btn-group d-flex mt-3">
          <div className="label text-primary font-weight-bolder mt-2">{config.versionLabel}</div>
          <div className="dropdown dropdown-xs">
            <button className="btn-dropdown dropdown-toggle pr-0" type="button" id="versionDropdown" data-bs-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
              {versionName}
            </button>
            <div className="dropdown-menu" aria-labelledby="versionDropdown">
              {(config.versions || []).map((item) => (
                <button
                  key={`version-${item}`}
                  className={`dropdown-item ${item === versionName ? "active" : ""}`}
                  type="button"
                  onClick={() => updateFilter(setVersionName, item)}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>
          <div className="col-lg-6">
            <button type="button" onClick={() => void openVersionDetails()} className="btn btn-link px-0 text-underline">
              {config.versionDetailsLabel}
            </button>
            <button type="button" onClick={downloadVersion} className="btn btn-primary ml-3">
              {config.downloadVersionLabel}
            </button>
          </div>
        </div>
      </div>

      {hasSearched ? (
        <div className="mt-3">
          {!loading && total <= 0 ? (
            <h4>{config.noResultsLabel}</h4>
          ) : null}

          {!loading && total > 0 ? (
            <>
              <div className="row mt-4 mb-3">
                <div className="col-lg d-flex align-items-center mt-3">
                  <p className="m-0">
                    {total} {resultsLabel}
                  </p>
                  <a className="btn btn-primary ml-3" href={downloadUrl} target="_blank" rel="noopener noreferrer">
                    {config.downloadLabel}
                  </a>
                </div>
                <div className="col-lg d-flex align-items-center justify-content-lg-end mt-3">
                  <span className="flex-shrink-0 mr-2">{config.itemsPerPageLabel}</span>
                  <div className="dropdown dropdown-xs">
                    <button className="btn-dropdown dropdown-toggle pr-0" type="button" id="itemPerPageDropdown" data-bs-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                      {itemPerPage}
                    </button>
                    <div className="dropdown-menu" aria-labelledby="itemPerPageDropdown">
                      {itemPerPageOptions.map((item) => (
                        <button
                          key={`item-per-page-${item}`}
                          className={`dropdown-item ${item === itemPerPage ? "active" : ""}`}
                          type="button"
                          onClick={() => updateFilter(setItemPerPage, item)}
                        >
                          {item}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <table className="collapse-table">
                <thead>
                  <tr>
                    <th>
                      <SortToggle label={config.userTypeLabel} sortKey="User Type" activeSort={sort} orderByAscending={orderByAscending} onClick={toggleSort} />
                    </th>
                    <th>
                      <SortToggle label={config.areaLabel} sortKey="Area" activeSort={sort} orderByAscending={orderByAscending} onClick={toggleSort} />
                    </th>
                    <th>
                      <SortToggle label={config.itemLabel} sortKey="Item" activeSort={sort} orderByAscending={orderByAscending} onClick={toggleSort} />
                    </th>
                    <th>
                      <SortToggle label={config.viewTypeLabel} sortKey="View Type" activeSort={sort} orderByAscending={orderByAscending} onClick={toggleSort} />
                    </th>
                    <th>
                      <SortToggle label={config.permissionLabel} sortKey="Permission" activeSort={sort} orderByAscending={orderByAscending} onClick={toggleSort} />
                    </th>
                    <th>
                      <div className="d-flex align-items-center">
                        <span className="mr-2">{config.permissionConditionLabel}</span>
                      </div>
                    </th>
                    <th>
                      <div className="d-flex align-items-center">
                        <span className="mr-2">{config.derivationLabel}</span>
                      </div>
                    </th>
                    <th>
                      <div className="d-flex align-items-center">
                        <span className="mr-2">{config.reccoDescriptionLabel}</span>
                      </div>
                    </th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {searchResults.map((item, index) => (
                    <tr key={`${item.item || "item"}-${index}`}>
                      <td data-title={config.userTypeLabel}>{item.userType}</td>
                      <td data-title={config.areaLabel}>{item.area}</td>
                      <td data-title={config.itemLabel}>{item.item}</td>
                      <td data-title={config.viewTypeLabel}>{item.viewType}</td>
                      <td data-title={config.permissionLabel}>{item.permission}</td>
                      <td data-title={config.permissionConditionLabel}>{item.permissionCondition}</td>
                      <td data-title={config.derivationLabel}>{item.derivation}</td>
                      <td data-title={config.reccoDescriptionLabel}>{item.recDescription}</td>
                      <td data-title={config.itemDetailsLabel}>
                        <button
                          type="button"
                          onClick={() => openItemDetailsModal(item.item, item.itemGlossary)}
                          className="btn btn-link px-0 text-underline"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="mr-2" viewBox="0 0 16 16">
                            <path
                              fillRule="evenodd"
                              d="M15 2a1 1 0 0 0-1-1H2a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V2zM0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V2zm8.5 9.5a.5.5 0 0 1-1 0V5.707L5.354 7.854a.5.5 0 1 1-.708-.708l3-3a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1-.708.708L8.5 5.707V11.5z"
                            />
                          </svg>
                          <span>{config.itemDetailsLabel}</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {total > itemPerPage ? (
                <div className="mt-4">
                  <Pagination
                    currentPage={currentPage}
                    pageSize={itemPerPage}
                    totalItems={total}
                    onPageChange={(pageNumber) => {
                      setCurrentPage(pageNumber);
                      setHasSearched(true);
                    }}
                    previousLabel={config.previousPageLabel}
                    nextLabel={config.nextPageLabel}
                  />
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}

      <div
        className="modal custom-modal fade"
        id="dpmItemDetailsModal"
        data-backdrop="static"
        data-keyboard="false"
        tabIndex="-1"
        aria-labelledby="dpmItemDetailsModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content rounded-xl">
            <div className="modal-header d-flex align-items-center">
              <h4 className="modal-title font-weight-bold m-0" id="dpmItemDetailsModalLabel">
                {config.itemDetailsModalTitlePrefix} {selectedItem}
              </h4>
              <button type="button" className="close mb-3 ml-auto" data-bs-dismiss="modal" aria-label={config.closeLabel}>
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div className="modal-body">
              {selectedGlossary.length > 0 ? (
                <ul className="border border-primary rounded-lg mt-2">
                  {selectedGlossary.map((glossaryItem, index) => (
                    <li key={`itemGlossary-${index}`} className="m-2">
                      {glossaryItem?.description}
                    </li>
                  ))}
                </ul>
              ) : (
                <ul className="border border-primary rounded-lg mt-2">
                  <li className="m-2">{selectedItem}</li>
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
